import React, { useEffect, useState } from "react";
import { Mail, MapPin, Clock, Send, ArrowRight } from "lucide-react";
import emailjs from "@emailjs/browser";
import { db } from "../firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import RfPageFrame from "../layout/RfPageFrame";
import { emailjsConfig } from "../config";

const { serviceId: SERVICE_ID, templateId: TEMPLATE_ID, publicKey: PUBLIC_KEY } = emailjsConfig;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // "success" | "error" | null
  const [errorDetail, setErrorDetail] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const { name, email, subject, message } = formData;
    setErrorDetail("");

    // Step 1: Firestore — non-blocking, failure does not prevent email from sending
    try {
      await addDoc(collection(db, "contactMessagesRakfortWebsite"), {
        name,
        email,
        subject,
        message,
        createdAt: serverTimestamp(),
        status: "new",
      });
    } catch (firestoreError) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Firestore write failed:", firestoreError);
      }
      // Continue — don't block the email send
    }

    // Step 2: EmailJS — this is the primary delivery mechanism
    try {
      const res = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        { from_name: name, from_email: email, subject, message },
        PUBLIC_KEY
      );

      if (res.status === 200) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setErrorDetail(`EmailJS returned status ${res.status}`);
        setSubmitStatus("error");
      }
    } catch (emailError) {
      if (process.env.NODE_ENV === 'development') {
        console.error("EmailJS send failed:", emailError);
      }
      setErrorDetail(process.env.NODE_ENV === 'development' ? emailError.message || String(emailError) : "");
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 8000);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Our Location",
      description: "Dublin, Ireland",
      link: null,
    },
    {
      icon: Mail,
      title: "Email Us",
      description: "info@rakfort.com",
      link: "mailto:info@rakfort.com",
    },
    {
      icon: Clock,
      title: "Working Hours",
      description: "Mon – Fri: 9:00 AM – 6:00 PM",
      details: "Saturday – Sunday: Closed",
    },
  ];

  return (
    <div className="overflow-x-hidden">

      {/* Hero */}
      <div className="rf-hero-band border-b border-[#e5eef6]">
        <RfPageFrame className="!py-16 md:!py-24">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#005a9c]/5 border border-[#005a9c]/10 text-[#005a9c] text-xs font-bold tracking-wider uppercase">
              <Mail className="w-3 h-3" />
              Contact Us
            </div>
            <h1 className="text-4xl md:text-[3.5rem] font-bold text-[#2c2c2c] leading-[1.1] tracking-tight">
              Get in <span className="text-[#005a9c]">Touch</span>
            </h1>
            <p className="text-[1.15rem] text-[#5c5c5c] leading-relaxed">
              Have questions, feedback, or partnership inquiries? We'd love to hear from you.
            </p>
          </div>
        </RfPageFrame>
      </div>

      {/* Main Content */}
      <div className="bg-[#f0f7ff] border-b border-[#e5eef6] py-16 md:py-24">
        <RfPageFrame className="!py-0">
          <div className="grid lg:grid-cols-2 gap-12 items-start">

            {/* Left — info */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-[#2c2c2c]">Let's Connect</h2>
                <p className="text-[#5c5c5c] text-[1.05rem] leading-relaxed">
                  Whether you're interested in collaboration, have a question,
                  or just want to say hello, our team is always happy to connect with you.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                {contactInfo.map((item) => (
                  <div
                    key={item.title}
                    className="rf-card p-6 bg-white hover:border-[#005a9c]/30 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-[#005a9c]/5 border border-[#005a9c]/10 flex items-center justify-center shrink-0">
                        <item.icon className="w-5 h-5 text-[#005a9c]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#2c2c2c] text-sm">{item.title}</h3>
                        {item.link ? (
                          <a href={item.link} className="text-[#005a9c] hover:underline text-sm mt-0.5 block">
                            {item.description}
                          </a>
                        ) : (
                          <p className="text-[#5c5c5c] text-sm mt-0.5">{item.description}</p>
                        )}
                        {item.details && (
                          <p className="text-xs text-slate-400 mt-0.5">{item.details}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-sm font-bold uppercase tracking-widest text-[#2c2c2c] mb-4">Follow Us</h3>
                <div className="flex gap-3">
                  {[
                    { name: "Twitter / X", url: "https://x.com/rakfortltd", label: "X" },
                    { name: "LinkedIn", url: "https://www.linkedin.com/company/rakfort/", label: "in" },
                    { name: "GitHub", url: "https://github.com/rakfortltd", label: "GH" },
                  ].map((social) => (
                    <a
                      key={social.name}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:border-[#005a9c] hover:text-[#005a9c] text-[#5c5c5c] text-xs font-bold transition-colors shadow-sm"
                    >
                      {social.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div className="rf-card p-8 bg-white shadow-xl">
              <h2 className="text-2xl font-bold text-[#2c2c2c] mb-6">Send Us a Message</h2>

              {submitStatus === "success" && (
                <div className="p-3 mb-6 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                  Thank you! Your message has been sent. We'll get back to you soon.
                </div>
              )}
              {submitStatus === "error" && (
                <div className="p-3 mb-6 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  Something went wrong. Please try again or email us directly at info@rakfort.com
                  {errorDetail && (
                    <p className="mt-1 font-mono text-xs text-red-500">{errorDetail}</p>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {[
                  { label: "Full Name", name: "name", type: "text", placeholder: "Your name" },
                  { label: "Email Address", name: "email", type: "email", placeholder: "your.email@example.com" },
                  { label: "Subject", name: "subject", type: "text", placeholder: "Subject" },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-[#5c5c5c] mb-2">
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      required
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005a9c] focus:border-transparent text-[#2c2c2c] placeholder-slate-400 text-sm transition-all"
                    />
                  </div>
                ))}

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-[#5c5c5c] mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project or inquiry..."
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#005a9c] focus:border-transparent text-[#2c2c2c] placeholder-slate-400 text-sm resize-none transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex items-center justify-center gap-2 py-4 px-6 bg-[#005a9c] text-white font-bold rounded-lg hover:bg-[#004a82] transition-all shadow-lg shadow-[#005a9c]/20 ${
                    isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

          </div>
        </RfPageFrame>
      </div>

      {/* Location */}
      <div className="bg-white py-16 md:py-24">
        <RfPageFrame className="!py-0">
          <div className="rf-card overflow-hidden bg-gradient-to-br from-[#005a9c]/5 to-slate-50 border border-slate-100">
            <div className="h-80 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-[#005a9c]/10 border border-[#005a9c]/20 flex items-center justify-center mx-auto">
                  <MapPin className="w-7 h-7 text-[#005a9c]" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#2c2c2c]">Our Office</h3>
                  <p className="text-[#5c5c5c] mt-1">Dublin, Ireland</p>
                </div>
                <a
                  href="https://maps.google.com/?q=Dublin,Ireland"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#005a9c] text-white font-bold rounded-lg hover:bg-[#004a82] transition-all shadow-lg shadow-[#005a9c]/20"
                >
                  <MapPin className="w-4 h-4" />
                  View on Map
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </RfPageFrame>
      </div>

    </div>
  );
};

export default ContactUs;
